/**
 * Utility functions for managing dynamic meta tags
 */


export interface MetaTags {
  title: string;
  description: string;
  image: string;
  url?: string;
  type?: string;
}

/**
 * Updates the document title
 */
export const updateTitle = (title: string) => {
  document.title = title;
};

/**
 * Updates or creates a meta tag
 */
export const updateMetaTag = (property: string, content: string, isProperty = true) => {
  const attribute = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, property);
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Updates Open Graph meta tags
 */
export const updateOpenGraphTags = (meta: MetaTags) => {
  updateMetaTag('og:title', meta.title);
  updateMetaTag('og:description', meta.description);
  updateMetaTag('og:image', meta.image);
  updateMetaTag('og:url', meta.url || window.location.href);
  updateMetaTag('og:type', meta.type || 'article');
};

/**
 * Updates Twitter Card meta tags
 */
export const updateTwitterTags = (meta: MetaTags) => {
  updateMetaTag('twitter:title', meta.title);
  updateMetaTag('twitter:description', meta.description);
  updateMetaTag('twitter:image', meta.image);
};

/**
 * Updates standard meta tags
 */
export const updateStandardMetaTags = (meta: MetaTags) => {
  updateMetaTag('description', meta.description, false);
  updateMetaTag('author', 'Chirag Gadhvi', false);
};

/**
 * Updates all meta tags for a blog post
 */
export const updateBlogPostMetaTags = (post: {
  title: string;
  mainImage?: any;
  body?: Array<any>;
  slug: { current: string };
}, imageFromBuilder?: string | null) => {
  // imageFromBuilder is passed in so this file doesn't pull the Sanity client into the home bundle
  // Extract description from body content
  const getDescription = (body: Array<any>, maxLength: number = 160) => {
    if (!body || body.length === 0) return "Read this blog post by Chirag Gadhvi";
    
    const textBlocks = body
      .filter((block) => block._type === "block")
      .map((block) =>
        block.children
          ?.map((child: any) => child.text)
          .join("")
      )
      .join(" ");
    
    const description = textBlocks.slice(0, maxLength) + (textBlocks.length > maxLength ? "..." : "");
    return description || "Read this blog post by Chirag Gadhvi";
  };

  // Get image URL using the proper helper
  let imageUrl = `${window.location.protocol}//${window.location.host}/og-image.png`; // Default fallback
  if (imageFromBuilder) {
    // Ensure it's an absolute URL
    imageUrl = imageFromBuilder.startsWith('http') 
      ? imageFromBuilder 
      : `https:${imageFromBuilder}`;
  }

  const meta: MetaTags = {
    title: `${post.title} — Chirag Gadhvi`,
    description: getDescription(post.body),
    image: imageUrl,
    url: `${window.location.protocol}//${window.location.host}/blogs/${post.slug.current}`,
    type: 'article'
  };

  // Update all meta tags
  updateTitle(meta.title);
  updateOpenGraphTags(meta);
  updateTwitterTags(meta);
  updateStandardMetaTags(meta);
};

/**
 * Resets meta tags to default homepage values
 */
export const resetToDefaultMetaTags = () => {
  const defaultMeta: MetaTags = {
    title: "Chirag Gadhvi — Software Engineer",
    description: "DevOps Engineer with 2+ years of experience automating cloud infrastructure, CI/CD pipelines, and AI-powered automation tools. Based in Hyderabad, India.",
    image: `${window.location.protocol}//${window.location.host}/og-image.png`,
    url: window.location.href,
    type: 'website'
  };

  updateTitle(defaultMeta.title);
  updateOpenGraphTags(defaultMeta);
  updateTwitterTags(defaultMeta);
  updateStandardMetaTags(defaultMeta);
};
